import { useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { IglooCustomPin, RawIglooCustomPinResponse } from '@/models'

import { normalizeIglooCustomPin } from './normalize-igloo-custom-pin.utils'

type GetIglooCustomPinRequest = { clientId: string }

// Note: `pending` waits on the resident finishing at the lock, `revoked` waits on staff completing
// the Elevated Hub sync. Neither event reaches the browser any other way, so the query polls while
// the PIN sits in one of them and stops once it settles on `active` or leaves the response.
const REFETCH_INTERVAL = 30000

export const getIglooCustomPinQueryKey = (data: GetIglooCustomPinRequest) => [
  'useGetIglooCustomPin',
  data,
]

const getIglooCustomPin = async (data: GetIglooCustomPinRequest): Promise<IglooCustomPin> => {
  const response = await authAPI.get<RawIglooCustomPinResponse>(
    `/igloowork/${data.clientId}/custom-pin`,
  )
  return normalizeIglooCustomPin(response.data)
}

export const useGetIglooCustomPin = (data: GetIglooCustomPinRequest, enabled = true) =>
  useQuery({
    enabled: enabled && Boolean(data.clientId),
    queryFn: () => getIglooCustomPin(data),
    queryKey: getIglooCustomPinQueryKey(data),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'pending' || status === 'revoked' ? REFETCH_INTERVAL : false
    },
  })

export const useInvalidateIglooCustomPin = (clientId: string) => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: getIglooCustomPinQueryKey({ clientId }) })
}
